import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const communicationsMessagesCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(120),
    suppressedLabel: z.string().trim().min(1).max(240),
});
export const communicationsMessagesCopySetting = definePlatformSetting({
    definition: {
        key: 'communications.copy.messages',
        moduleId: 'communications',
        title: 'Messages interface copy',
        helpText: 'Controls tenant-editable labels in the Communications history surfaces.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Messages',
            title: 'Messages',
            description: 'Email and text history with customers and partners.',
            emptyTitle: 'No messages yet',
            emptyDescription: 'Conversations will appear here as they happen.',
            loadingLabel: 'Loading messages…',
            suppressedLabel: 'Not sent — this contact has asked not to be contacted on this channel.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: communicationsMessagesCopyValueSchema,
});
export const communicationsPortalProjectMessagesConfigurationValueSchema = z.strictObject({
    enabled: z.boolean(),
    staffProjectPanelEnabled: z.boolean(),
    allowCustomerMessages: z.boolean(),
    allowStaffReplies: z.boolean(),
    maximumMessageCharacters: z.number().int().min(100).max(2_000),
    notificationPreviewCharacters: z.number().int().min(50).max(500),
    notificationFallbackRoleKeys: z.array(z.string().trim().min(1).max(80).regex(/^[a-z][a-z0-9-]*$/)).min(1).max(10).refine((roleKeys) => new Set(roleKeys).size === roleKeys.length, 'Portal message notification fallback roles must be unique.'),
});
export const communicationsPortalProjectMessagesConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'communications.portal-messages.configuration',
        moduleId: 'communications',
        title: 'Customer Portal messages behavior',
        helpText: 'Controls Portal message availability, bounded message size, staff replies, and the durable internal-notification audience intent.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            enabled: true,
            staffProjectPanelEnabled: true,
            allowCustomerMessages: true,
            allowStaffReplies: true,
            maximumMessageCharacters: 2_000,
            notificationPreviewCharacters: 200,
            notificationFallbackRoleKeys: ['owner'],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'protected',
    },
    valueSchema: communicationsPortalProjectMessagesConfigurationValueSchema,
});
export const communicationsPortalProjectMessagesDisplayValueSchema = z.strictObject({
    enabled: z.boolean(),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    density: z.enum(['compact', 'comfortable']),
    showSenderNames: z.boolean(),
    showTimestamps: z.boolean(),
    maximumVisibleMessages: z.number().int().min(10).max(100),
});
export const communicationsPortalProjectMessagesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'communications.portal-messages.display',
        moduleId: 'communications',
        title: 'Customer Portal messages display',
        helpText: 'Controls Portal message card size, density, sender labels, timestamps, and visible history.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            enabled: true,
            cardSize: 'standard',
            density: 'comfortable',
            showSenderNames: true,
            showTimestamps: true,
            maximumVisibleMessages: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: communicationsPortalProjectMessagesDisplayValueSchema,
});
export const communicationsPortalProjectMessagesCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(240),
    emptyLabel: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(120),
    unavailableLabel: z.string().trim().min(1).max(240),
    composerLabel: z.string().trim().min(1).max(120),
    composerPlaceholder: z.string().trim().min(1).max(200),
    sendAction: z.string().trim().min(1).max(80),
    sendingAction: z.string().trim().min(1).max(80),
    sentConfirmation: z.string().trim().min(1).max(200),
    sendFailureRetainedDraft: z.string().trim().min(1).max(300),
    customerSenderFallback: z.string().trim().min(1).max(100),
    staffSenderFallback: z.string().trim().min(1).max(100),
    customerDirectionLabel: z.string().trim().min(1).max(80),
    staffDirectionLabel: z.string().trim().min(1).max(80),
    needsReplyLabel: z.string().trim().min(1).max(120),
    waitingOnCustomerLabel: z.string().trim().min(1).max(120),
});
export const communicationsPortalProjectMessagesCopySetting = definePlatformSetting({
    definition: {
        key: 'communications.copy.portal-messages',
        moduleId: 'communications',
        title: 'Customer Portal messages copy',
        helpText: 'Controls customer and staff wording for the Portal message thread.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            title: 'Messages',
            description: 'Keep project questions and replies together in one secure thread.',
            emptyLabel: 'No messages yet. Send a message to the project team below.',
            loadingLabel: 'Loading messages…',
            unavailableLabel: 'Messages are not available right now.',
            composerLabel: 'Message',
            composerPlaceholder: 'Type a message…',
            sendAction: 'Send',
            sendingAction: 'Sending…',
            sentConfirmation: 'Your message was added to the project thread.',
            sendFailureRetainedDraft: 'Your message did not send. Please try again; your text is still here.',
            customerSenderFallback: 'Customer',
            staffSenderFallback: 'Project team',
            customerDirectionLabel: 'Customer',
            staffDirectionLabel: 'Team',
            needsReplyLabel: 'Needs reply',
            waitingOnCustomerLabel: 'Waiting on customer',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: communicationsPortalProjectMessagesCopyValueSchema,
});
export const communicationsSettingRegistrations = [
    communicationsMessagesCopySetting,
    communicationsPortalProjectMessagesConfigurationSetting,
    communicationsPortalProjectMessagesDisplaySetting,
    communicationsPortalProjectMessagesCopySetting,
];
//# sourceMappingURL=communications-setting-definitions.js.map